import React, { useState, useEffect, useMemo } from 'react';
import { Card, Button, Modal, Form, Spinner, Row, Col, InputGroup, ButtonGroup } from 'react-bootstrap';
import { History, Pencil, Trash2, Calendar, List, Grid, Search, Filter, Download } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { showSuccess, showError, showConfirm } from '../../../utils/notifications';
import { ReservationHistory, House } from '../../../services/types';
import { formatDate } from '../../../utils/reservationHelpers';
import { exportReservationHistoryToExcel } from '../../../utils/excelExportHistory';
import reservationHistoryService from '../../../services/reservationHistoryService';
import housesService from '../../../services/housesService';
import ReservationDetailModal from '../../../components/reservations/ReservationDetailModal';
import HistoryCalendarView from '../../../components/reservation-history/HistoryCalendarView';
import HistoryListView from '../../../components/reservation-history/HistoryListView';
import DataTable, { ColumnDef, ActionButton, StatusBadge } from '../../../components/common/DataTable';
import { useUserData } from '../../../hooks';

type ViewMode = 'calendar' | 'table' | 'list';

const ReservationHistoryPage = () => {
    const { role } = useUserData();
    const [histories, setHistories] = useState<ReservationHistory[]>([]);
    const [houses, setHouses] = useState<House[]>([]);
    const [loading, setLoading] = useState(true);
    const [isMobile, setIsMobile] = useState(false);
    const [viewMode, setViewMode] = useState<ViewMode>('table');

    // Filters
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [houseFilter, setHouseFilter] = useState<string>('all');

    // Modal state
    const [showModal, setShowModal] = useState(false);
    const [editingHistory, setEditingHistory] = useState<ReservationHistory | null>(null);
    const [selectedHistory, setSelectedHistory] = useState<ReservationHistory | null>(null);
    const [showDetailModal, setShowDetailModal] = useState(false);

    type FormData = {
        id_reservation_platform: string;
        house_id: string;
        reservation_id: string;
        platform: string;
        name_first_guess: string;
        nationality: string;
        contact: string;
        baby: boolean;
        rooms: number;
        check_in_date: string;
        check_out_date: string;
        check_in_hour: string;
        check_out_hour: string;
        pets: boolean;
        deposit_number: string;
        reservation_state: string;
        observations: string;
        number_of_guests: string;
        user_id: string;
        action: string;
        is_paid: boolean;
    };

    const [formData, setFormData] = useState<FormData>({
        id_reservation_platform: '',
        house_id: '',
        reservation_id: '',
        platform: '',
        name_first_guess: '',
        nationality: '',
        contact: '',
        baby: false,
        rooms: 1,
        check_in_date: '',
        check_out_date: '',
        check_in_hour: '',
        check_out_hour: '',
        pets: false,
        deposit_number: '',
        reservation_state: 'pending',
        observations: '',
        number_of_guests: '',
        user_id: '',
        action: '',
        is_paid: false,
    });
    const [saving, setSaving] = useState(false);

    // Detect mobile and force list view
    useEffect(() => {
        const checkMobile = () => {
            const mobile = window.innerWidth < 768;
            setIsMobile(mobile);
            // Force list view on mobile (table view not available)
            if (mobile && viewMode === 'table') {
                setViewMode('list');
            }
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        fetchHistories();
        fetchHouses(); // Still needed for filters and form dropdowns
    }, []);

    // Filtered histories
    const filteredHistories = useMemo(() => {
        return histories.filter((history) => {
            // Search filter
            const matchesSearch = searchQuery === '' ||
                history.name_first_guess?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                history.contact.toLowerCase().includes(searchQuery.toLowerCase());

            // Status filter
            const matchesStatus = statusFilter === 'all' ||
                history.reservation_state === statusFilter;

            // House filter
            const matchesHouse = houseFilter === 'all' ||
                history.house_id === houseFilter;

            return matchesSearch && matchesStatus && matchesHouse;
        });
    }, [histories, searchQuery, statusFilter, houseFilter]);

    const handleSelectHistory = (history: ReservationHistory) => {
        setSelectedHistory(history);
        setShowDetailModal(true);
    };

    const fetchHistories = async () => {
        try {
            setLoading(true);
            const data = await reservationHistoryService.getAll();
            setHistories(Array.isArray(data) ? data : []);
        } catch (err: any) {
            const errorMessage = err.response?.data?.message || err.message || 'Failed to load histories';
            console.error('Error loading histories:', err);
            setHistories([]);
            showError('Error!', errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const fetchHouses = async () => {
        try {
            const data = await housesService.getAll();
            setHouses(Array.isArray(data) ? data : []);
        } catch (err: any) {
            console.error('Error loading houses:', err);
        }
    };


    const handleAdd = () => {
        setEditingHistory(null);
        setFormData({
            id_reservation_platform: '',
            house_id: '',
            reservation_id: '',
            platform: '',
            name_first_guess: '',
            nationality: '',
            contact: '',
            baby: false,
            rooms: 1,
            check_in_date: '',
            check_out_date: '',
            check_in_hour: '',
            check_out_hour: '',
            pets: false,
            deposit_number: '',
            reservation_state: 'pending',
            observations: '',
            number_of_guests: '',
            user_id: '',
            action: '',
            is_paid: false,
        });
        setShowModal(true);
    };

    const handleEdit = (history: ReservationHistory) => {
        setEditingHistory(history);
        setFormData({
            id_reservation_platform: history.id_reservation_platform || '',
            house_id: history.house_id || '',
            reservation_id: history.reservation_id || '',
            platform: history.platform,
            name_first_guess: history.name_first_guess,
            nationality: history.nationality || '',
            contact: history.contact,
            baby: history.baby,
            rooms: history.rooms,
            check_in_date: history.check_in_date,
            check_out_date: history.check_out_date,
            check_in_hour: history.check_in_hour || '',
            check_out_hour: history.check_out_hour || '',
            pets: history.pets,
            deposit_number: history.deposit_number?.toString() || '',
            reservation_state: history.reservation_state,
            observations: history.observations || '',
            number_of_guests: history.number_of_guests?.toString() || '',
            user_id: history.user_id || '',
            action: history.action || '',
            is_paid: history.is_paid,
        });
        setShowModal(true);
    };

    const handleDelete = async (publicId: string) => {
        const confirmed = await showConfirm(
            'Are you sure?',
            'You won\'t be able to revert this!',
            'Yes, delete it!',
            'Cancel'
        );

        if (!confirmed) return;

        try {
            await reservationHistoryService.delete(publicId);
            setHistories(histories.filter(h => h.public_id !== publicId));
            showSuccess('Deleted!', 'History record has been deleted successfully.');
        } catch (err: any) {
            showError('Error!', err.response?.data?.message || 'Failed to delete history');
            console.error('Error deleting history:', err);
        }
    };

    const validateForm = (): boolean => {
        if (!formData.platform.trim()) {
            showError('Validation Error', 'Platform is required');
            return false;
        }
        if (!formData.name_first_guess.trim()) {
            showError('Validation Error', 'Guest name is required');
            return false;
        }
        if (!formData.contact.trim()) {
            showError('Validation Error', 'Contact is required');
            return false;
        }
        if (formData.rooms < 1) {
            showError('Validation Error', 'Rooms must be at least 1');
            return false;
        }
        if (!formData.check_in_date) {
            showError('Validation Error', 'Check-in date is required');
            return false;
        }
        if (!formData.check_out_date) {
            showError('Validation Error', 'Check-out date is required');
            return false;
        }
        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        try {
            setSaving(true);

            const payload = {
                id_reservation_platform: formData.id_reservation_platform || null,
                house_id: formData.house_id || null,
                reservation_id: formData.reservation_id || null,
                platform: formData.platform,
                name_first_guess: formData.name_first_guess,
                nationality: formData.nationality || null,
                contact: formData.contact,
                baby: formData.baby,
                rooms: formData.rooms,
                check_in_date: formData.check_in_date,
                check_out_date: formData.check_out_date,
                check_in_hour: formData.check_in_hour || null,
                check_out_hour: formData.check_out_hour || null,
                pets: formData.pets,
                deposit_number: formData.deposit_number ? parseFloat(formData.deposit_number) : null,
                reservation_state: formData.reservation_state,
                observations: formData.observations || null,
                number_of_guests: formData.number_of_guests ? parseInt(formData.number_of_guests) : null,
                user_id: formData.user_id || null,
                action: formData.action || null,
                is_paid: formData.is_paid,
            };

            if (editingHistory) {
                const updated = await reservationHistoryService.update(editingHistory.public_id, payload);
                setHistories(histories.map(h =>
                    h.public_id === editingHistory.public_id ? updated : h
                ));
                showSuccess('Updated!', 'History record has been updated successfully.');
            } else {
                const newHistory = await reservationHistoryService.create(payload);
                setHistories([...histories, newHistory]);
                showSuccess('Created!', 'History record has been created successfully.');
            }

            setShowModal(false);
        } catch (err: any) {
            showError('Error!', err.response?.data?.message || 'Failed to save history');
            console.error('Error saving history:', err);
        } finally {
            setSaving(false);
        }
    };

    const getHouseName = (history: ReservationHistory): string => {
        // Use house_name from reservation history if available
        if (history.house_name) return history.house_name;
        // Fallback to lookup in houses array
        if (history.house_id) {
            const house = houses.find(h => h.public_id === history.house_id);
            return house ? (house.name || house.address) : 'Unknown';
        }
        return 'N/A';
    };

    const handleDownloadExcel = () => {
        try {
            exportReservationHistoryToExcel(filteredHistories, houses);
            showSuccess('Success!', 'Reservation history exported to Excel successfully.');
        } catch (err: any) {
            showError('Error!', 'Failed to export reservation history to Excel. Please try again.');
            console.error('Error exporting to Excel:', err);
        }
    };



    const renderTableView = () => {
        if (!Array.isArray(filteredHistories) || filteredHistories.length === 0) {
            return (
                <Card className="border-0 shadow-sm">
                    <Card.Body className="text-center py-5">
                        <p className="text-muted">No history records found</p>
                    </Card.Body>
                </Card>
            );
        }

        // Define table columns
        const columns: ColumnDef<ReservationHistory>[] = [
            {
                key: 'reservation_state',
                header: 'Status',
                sortable: true,
                render: (history) => (
                    <StatusBadge
                        status={history.reservation_state === 'confirmed' ? 'Confirmed' : history.reservation_state === 'cancelled' ? 'Cancelled' : 'Pending'}
                        variant={history.reservation_state === 'confirmed' ? 'success' : history.reservation_state === 'cancelled' ? 'danger' : 'warning'}
                    />
                ),
            },
            {
                key: 'name_first_guess',
                header: 'Guest',
                sortable: true,
                render: (history) => (
                    <div style={{ fontWeight: '500', color: '#111827' }}>
                        {history.name_first_guess}
                    </div>
                ),
            },
            {
                key: 'platform',
                header: 'Platform',
                sortable: true,
                render: (history) => (
                    <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                        {history.platform}
                    </div>
                ),
            },
            {
                key: 'house_id',
                header: 'House',
                sortable: true,
                render: (history) => (
                    <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                        {getHouseName(history)}
                    </div>
                ),
            },
            {
                key: 'user_name',
                header: 'User',
                sortable: true,
                render: (history) => (
                    <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                        {history.user_name || '-'}
                    </div>
                ),
            },
            {
                key: 'check_in_date',
                header: 'Check-in',
                sortable: true,
                render: (history) => (
                    <div style={{ color: '#374151', fontSize: '0.875rem' }}>
                        {formatDate(history.check_in_date, 'MMM dd, yyyy')}
                    </div>
                ),
            },
            {
                key: 'check_out_date',
                header: 'Check-out',
                sortable: true,
                render: (history) => (
                    <div style={{ color: '#374151', fontSize: '0.875rem' }}>
                        {formatDate(history.check_out_date, 'MMM dd, yyyy')}
                    </div>
                ),
            },
            {
                key: 'action',
                header: 'Action',
                sortable: true,
                render: (history) => history.action ? (
                    <StatusBadge status={history.action} variant="info" />
                ) : (
                    <span style={{ color: '#9ca3af' }}>-</span>
                ),
            },
            {
                key: 'is_paid',
                header: 'Paid',
                sortable: true,
                render: (history) => (
                    <StatusBadge
                        status={history.is_paid ? 'Yes' : 'No'}
                        variant={history.is_paid ? 'success' : 'secondary'}
                    />
                ),
            },
        ];

        // Define table actions
        const actions: ActionButton<ReservationHistory>[] = [
            {
                label: 'Delete',
                icon: <Trash2 size={14} />,
                variant: 'danger',
                onClick: (history) => handleDelete(history.public_id),
            },
        ];

        return (
            <DataTable
                columns={columns}
                data={filteredHistories}
                actions={actions}
                onRowClick={handleSelectHistory}
                emptyMessage="No history records found"
                keyExtractor={(history) => history.public_id}
            />
        );
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 text-muted">Loading reservation history...</p>
            </div>
        );
    }

    return (
        <div>
            {/* Header */}
            <div className={`d-flex ${isMobile ? 'flex-column gap-3' : 'justify-content-between align-items-center'} mb-4`}>
                {!isMobile && (
                    <div>
                        <h4 className="mb-1">History Management</h4>
                        <p className="text-muted small mb-0">
                            {filteredHistories.length} of {histories.length} history records
                        </p>
                    </div>
                )}
                {isMobile && (
                    <div className="text-muted small mb-0">
                        {filteredHistories.length} of {histories.length} records
                    </div>
                )}
                {/* Super Admin only: Download Excel button */}
                {role === 'Super Admin' && (
                    <Button
                        variant="outline-success"
                        onClick={handleDownloadExcel}
                        className={isMobile ? 'w-100' : ''}
                        title="Export reservation history to Excel"
                    >
                        <Download size={18} className="me-2" />
                        {isMobile ? 'Download Excel' : 'Download Excel'}
                    </Button>
                )}
            </div>

            {/* Filters and View Toggle */}
            <Card className="border-0 shadow-sm mb-4">
                <Card.Body>
                    <div className="row g-3 align-items-end">
                        {/* Search */}
                        <div className="col-md-4">
                            <Form.Label className="small text-muted mb-1">Search</Form.Label>
                            <InputGroup>
                                <InputGroup.Text className="bg-white">
                                    <Search size={16} />
                                </InputGroup.Text>
                                <Form.Control
                                    placeholder="Search by guest or contact..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </InputGroup>
                        </div>

                        {/* Status Filter */}
                        <div className="col-md-3">
                            <Form.Label className="small text-muted mb-1">Status</Form.Label>
                            <InputGroup>
                                <InputGroup.Text className="bg-white">
                                    <Filter size={16} />
                                </InputGroup.Text>
                                <Form.Select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                >
                                    <option value="all">All Statuses</option>
                                    <option value="pending">Pending</option>
                                    <option value="confirmed">Confirmed</option>
                                    <option value="cancelled">Cancelled</option>
                                </Form.Select>
                            </InputGroup>
                        </div>

                        {/* House Filter */}
                        <div className="col-md-3">
                            <Form.Label className="small text-muted mb-1">House</Form.Label>
                            <Form.Select
                                value={houseFilter}
                                onChange={(e) => setHouseFilter(e.target.value)}
                            >
                                <option value="all">All Houses</option>
                                {houses.map((house) => (
                                    <option key={house.public_id} value={house.public_id}>
                                        {house.name || house.address}
                                    </option>
                                ))}
                            </Form.Select>
                        </div>

                        {/* View Toggle */}
                        <div className="col-md-2">
                            <ButtonGroup className="w-100">
                                <Button
                                    variant={viewMode === 'calendar' ? 'primary' : 'outline-primary'}
                                    onClick={() => setViewMode('calendar')}
                                    title="Calendar View"
                                >
                                    <Calendar size={18} />
                                </Button>

                                <Button
                                    variant={viewMode === 'list' ? 'primary' : 'outline-primary'}
                                    onClick={() => setViewMode('list')}
                                    title="List View"
                                >
                                    <List size={18} />
                                </Button>
                                <Button
                                    variant={viewMode === 'table' ? 'primary' : 'outline-primary'}
                                    onClick={() => setViewMode('table')}
                                    title={isMobile ? "Table view not available on mobile" : "Table View"}
                                    disabled={isMobile}
                                >
                                    <Grid size={18} />
                                </Button>
                            </ButtonGroup>
                        </div>
                    </div>
                </Card.Body>
            </Card>

            {/* View Content */}
            <AnimatePresence mode="wait">
                <motion.div
                    key={viewMode}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                >
                    {viewMode === 'calendar' && (
                        <HistoryCalendarView
                            reservations={filteredHistories}
                            houses={houses}
                            onSelectReservation={handleSelectHistory}
                        />
                    )}

                    {viewMode === 'table' && renderTableView()}

                    {viewMode === 'list' && (
                        <HistoryListView
                            reservations={filteredHistories}
                            houses={houses}
                            onSelectReservation={handleSelectHistory}
                        />
                    )}
                </motion.div>
            </AnimatePresence>

            {/* Reservation Detail Modal */}
            <ReservationDetailModal
                show={showDetailModal}
                onHide={() => setShowDetailModal(false)}
                reservation={selectedHistory}
                houseName={selectedHistory ? getHouseName(selectedHistory) : undefined}
            />

            {/* Add/Edit Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)} centered size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>{editingHistory ? 'Edit History Record' : 'Add History Record'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Platform <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={formData.platform}
                                        onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
                                        placeholder="e.g., Airbnb, Booking.com"
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Platform Reservation ID</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={formData.id_reservation_platform}
                                        onChange={(e) => setFormData({ ...formData, id_reservation_platform: e.target.value })}
                                        placeholder="Platform booking ID"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>House</Form.Label>
                                    <Form.Select
                                        value={formData.house_id}
                                        onChange={(e) => setFormData({ ...formData, house_id: e.target.value })}
                                    >
                                        <option value="">Select house (optional)</option>
                                        {houses.map((house) => (
                                            <option key={house.public_id} value={house.public_id}>
                                                {house.name || house.address}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Linked Reservation ID</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={formData.reservation_id}
                                        onChange={(e) => setFormData({ ...formData, reservation_id: e.target.value })}
                                        placeholder="Reservation public ID (optional)"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Guest Name <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={formData.name_first_guess}
                                        onChange={(e) => setFormData({ ...formData, name_first_guess: e.target.value })}
                                        placeholder="Enter guest name"
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Contact <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={formData.contact}
                                        onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                                        placeholder="Phone or email"
                                        required
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Check-in Date <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={formData.check_in_date}
                                        onChange={(e) => setFormData({ ...formData, check_in_date: e.target.value })}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Check-in Time</Form.Label>
                                    <Form.Control
                                        type="time"
                                        value={formData.check_in_hour}
                                        onChange={(e) => setFormData({ ...formData, check_in_hour: e.target.value })}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Rooms <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="1"
                                        value={formData.rooms}
                                        onChange={(e) => setFormData({ ...formData, rooms: parseInt(e.target.value) })}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Check-out Date <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={formData.check_out_date}
                                        onChange={(e) => setFormData({ ...formData, check_out_date: e.target.value })}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Check-out Time</Form.Label>
                                    <Form.Control
                                        type="time"
                                        value={formData.check_out_hour}
                                        onChange={(e) => setFormData({ ...formData, check_out_hour: e.target.value })}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Number of Guests</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="0"
                                        value={formData.number_of_guests}
                                        onChange={(e) => setFormData({ ...formData, number_of_guests: e.target.value })}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Nationality</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={formData.nationality}
                                        onChange={(e) => setFormData({ ...formData, nationality: e.target.value })}
                                        placeholder="Guest nationality"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Deposit Amount</Form.Label>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        value={formData.deposit_number}
                                        onChange={(e) => setFormData({ ...formData, deposit_number: e.target.value })}
                                        placeholder="0.00"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Action</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={formData.action}
                                        onChange={(e) => setFormData({ ...formData, action: e.target.value })}
                                        placeholder="e.g., created, updated, cancelled"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Status</Form.Label>
                                    <Form.Select
                                        value={formData.reservation_state}
                                        onChange={(e) => setFormData({ ...formData, reservation_state: e.target.value })}
                                    >
                                        <option value="pending">Pending</option>
                                        <option value="confirmed">Confirmed</option>
                                        <option value="cancelled">Cancelled</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>User ID</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={formData.user_id}
                                        onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
                                        placeholder="User public ID"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Check
                                        type="checkbox"
                                        label="Baby"
                                        checked={formData.baby}
                                        onChange={(e) => setFormData({ ...formData, baby: e.target.checked })}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Check
                                        type="checkbox"
                                        label="Pets"
                                        checked={formData.pets}
                                        onChange={(e) => setFormData({ ...formData, pets: e.target.checked })}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Check
                                        type="checkbox"
                                        label="Is Paid"
                                        checked={formData.is_paid}
                                        onChange={(e) => setFormData({ ...formData, is_paid: e.target.checked })}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Form.Group className="mb-3">
                            <Form.Label>Observations</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={formData.observations}
                                onChange={(e) => setFormData({ ...formData, observations: e.target.value })}
                                placeholder="Additional notes..."
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)} disabled={saving}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleSubmit} disabled={saving}>
                        {saving ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Saving...
                            </>
                        ) : (
                            editingHistory ? 'Update' : 'Create'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default ReservationHistoryPage;